/**
 * Plan-Parser (client.md §9.2). Das Plan-Dokument ist JSON mit führenden
 * `#`-Kommentar-Kopfzeilen: Kopfzeilen entfernen, dann JSON.parse.
 * Wirft der Parser, zeigt der Aufrufer (views/tabs/Plan.tsx) den Fallback (§9.2).
 */

export type PlanNodeStatus = "pending" | "running" | "done" | "failed" | "skipped" | "needs-fix";
export type PlanNodeKind = "for" | "while" | "if" | undefined;

export type PlanForEach = {
  id?: string;
  effort?: string;
  task?: string;
  produces?: string[];
};

export type PlanForSpec = {
  items_from?: string;
  items?: unknown[];
  each?: PlanForEach;
};

export type PlanNode = {
  id: string;
  task?: string;
  effort?: string;
  needs?: string[];
  produces?: string[];
  for?: PlanForSpec;
  while?: unknown;
  max_retries?: number;
  if?: unknown;
  then?: unknown;
  condition_result?: string;
  branch?: string;
  status?: PlanNodeStatus;
  result?: string;
  kind?: PlanNodeKind;
  /** normalisierte Kinder, egal ob im Rohschema "tasks" oder bereits "children" hieß */
  children?: PlanNode[];
};

export type ParsedPlan = {
  goal?: string;
  revision?: number;
  nodes: PlanNode[];
};

function normalizeNodes(raw: unknown): PlanNode[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((n) => {
    const node = n as PlanNode & { tasks?: PlanNode[] };
    const kind: PlanNodeKind = node.for ? "for" : node.while !== undefined ? "while" : node.if !== undefined ? "if" : undefined;
    const children = normalizeNodes(node.tasks ?? node.children);
    return { ...node, kind, children };
  });
}

/** Führende Kommentar-Kopfzeilen (# oder //) und Leerzeilen bis zum JSON-Body entfernen. */
function stripHeader(raw: string): string {
  const lines = raw.split(/\r?\n/);
  let start = 0;
  while (start < lines.length) {
    const t = lines[start].trim();
    if (t !== "" && !t.startsWith("#") && !t.startsWith("//")) break;
    start++;
  }
  return lines.slice(start).join("\n").trim();
}

export function parsePlan(raw: string): ParsedPlan {
  const data = JSON.parse(stripHeader(raw)) as Record<string, unknown> | null;
  if (data === null || typeof data !== "object" || Array.isArray(data)) {
    throw new Error("plan is not a JSON object");
  }
  const nodes = normalizeNodes((data.nodes ?? data.tasks) as unknown);
  return {
    goal: typeof data.goal === "string" ? data.goal : undefined,
    revision: typeof data.revision === "number" ? data.revision : undefined,
    nodes,
  };
}
